'use client';

import { useState } from 'react';
import {
  HelpCircle,
  Home,
  Menu,
  Search,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useIsMobile } from '@/hooks/use-mobile';

const logoIconSrc = '/logos/skill4skill-logo-icon.svg';

const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <nav className='sticky top-0 z-40 border-b border-gray-400/20 bg-[#FCFAF4]/80 backdrop-blur supports-[backdrop-filter]:bg-[#FCFAF4]/80'>
      <div className='mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8'>
        {/* Logo */}
        <Link
          href='/'
          className='flex items-center gap-2.5 text-xl text-brand-forest'
        >
          <Image
            src={logoIconSrc}
            alt='Skill4Skill Logo'
            width={36}
            height={36}
            className='size-9 object-contain'
            priority
          />
          <span className='font-bold'>Skill4Skill</span>
        </Link>

        {!isMobile ? (
          <>
            {/* Desktop nav — centered */}
            <nav
              aria-label='Primary'
              className='flex items-center gap-x-8 gap-y-4 text-sm font-medium text-foreground/80'
            >
              <NavLinks />
            </nav>

            {/* Auth buttons */}
            <div className='flex items-center gap-2'>
              <Link
                href='/auth/login'
                className='inline-flex h-10 items-center rounded-full px-4 text-sm font-medium text-brand-forest transition-colors hover:bg-[#5EB229] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              >
                Sign in
              </Link>
              <Link
                href='/auth/signup'
                className='inline-flex h-10 items-center rounded-full bg-brand-forest px-5 text-sm font-semibold text-white transition-colors hover:bg-[#02600E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5EB229] focus-visible:ring-offset-2'
              >
                Sign up
              </Link>
            </div>
          </>
        ) : (
          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            className='inline-flex size-10 items-center justify-center rounded-full text-brand-forest hover:bg-brand-forest/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            {mobileMenuOpen ? (
              <X className='size-5' />
            ) : (
              <Menu className='size-5' />
            )}
          </button>
        )}
      </div>

      {/* Mobile menu */}
      {isMobile && mobileMenuOpen && (
        <div className='border-t border-border/60 bg-background'>
          <div className='mx-auto flex max-w-7xl flex-col px-4 py-3 sm:px-6'>
            <NavLinks mobile />
            <div className='mt-2 flex gap-2 border-t border-border/60 pt-2'>
              <Link
                href='/auth/login'
                onClick={() => setMobileMenuOpen(false)}
                className='inline-flex h-10 flex-1 items-center justify-center rounded-full px-4 text-sm font-medium text-brand-forest transition-colors hover:bg-[#5EB229] hover:text-white'
              >
                Sign in
              </Link>
              <Link
                href='/auth/signup'
                onClick={() => setMobileMenuOpen(false)}
                className='inline-flex h-10 flex-1 items-center justify-center rounded-full bg-[#5EB229] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#02600E]'
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

const NavLinks = ({ mobile = false }: { mobile?: boolean }) => {
  const pathname = usePathname();

  const navItems = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Browse Skills', href: '/browse-skills', icon: Search },
    { name: 'Community', href: '/community', icon: Users },
    { name: 'Trust & Safety', href: '/trust', icon: ShieldCheck },
    { name: 'FAQs', href: '/faq', icon: HelpCircle },
  ];

  return (
    <>
      {navItems.map((item) => {
        const isActive =
          pathname === item.href ||
          (item.href !== '/' && pathname.startsWith(item.href));
        const Icon = item.icon;

        return (
          <Link
            key={item.name}
            href={item.href}
            className={`transition-colors hover:text-brand-forest ${
              mobile
                ? `flex items-center gap-3 rounded-md px-3 text-base font-medium ${
                    isActive
                      ? 'bg-brand-forest/10 font-semibold text-brand-forest'
                      : 'text-foreground/80'
                  }`
                : `flex flex-col items-center gap-1 text-center ${
                    isActive ? 'text-brand-forest' : 'text-gray-500'
                  }`
            }`}
          >
            <Icon className={mobile ? 'size-6' : 'size-6'} />
            <span className={mobile ? '' : 'text-xs'}>{item.name}</span>
          </Link>
        );
      })}
    </>
  );
};

export default NavBar;
